import axios from 'axios';

const api = axios.create({
  baseURL: '/api',
  headers: { 'Content-Type': 'application/json' },
});

api.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

api.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const detail = error.response?.data?.detail || '';
    const isLoginRequest = error.config?.url?.includes('/auth/login');
    if (!isLoginRequest) {
      if (
        status === 401 ||
        (status === 403 && (detail === 'Not authenticated' || detail === 'Insufficient permissions' || !localStorage.getItem('token')))
      ) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/login';
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export function getAuthFileUrl(url: string): string {
  if (!url || url.startsWith('data:')) return url;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  const token = localStorage.getItem('token');
  if (!token) return url;
  const separator = url.includes('?') ? '&' : '?';
  return `${url}${separator}token=${encodeURIComponent(token)}`;
}

export function getPhotoSrc(photo: any): string {
  if (!photo) return '';
  if (typeof photo === 'string') {
    if (photo.startsWith('data:')) return photo;
    if (photo.startsWith('http://') || photo.startsWith('https://')) return photo;
    return photo;
  }
  const url = photo.url || '';
  if (!url) return '';
  if (url.startsWith('data:')) return url;
  if (photo.storage === 'sharepoint' && photo.sharepoint_path) {
    const token = localStorage.getItem('token');
    const base = `/api/service-requests/sharepoint-photo?path=${encodeURIComponent(photo.sharepoint_path)}`;
    return token ? `${base}&token=${encodeURIComponent(token)}` : base;
  }
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  return getAuthFileUrl(url);
}

export function getAttachmentUrl(file: { url: string; storage?: string; sharepoint_path?: string }): string {
  if (!file.url) return '';
  if (file.storage === 'sharepoint' && file.sharepoint_path) {
    const token = localStorage.getItem('token');
    const base = `/api/projects/sharepoint-download?path=${encodeURIComponent(file.sharepoint_path)}`;
    return token ? `${base}&token=${encodeURIComponent(token)}` : base;
  }
  if (file.url.startsWith('/api/')) return getAuthFileUrl(file.url);
  return file.url;
}

export function formatMMK(value: number): string {
  if (value >= 100000) {
    const lakhs = value / 100000
    return `${lakhs % 1 === 0 ? lakhs.toFixed(0) : lakhs.toFixed(1)} Lakhs MMK`
  }
  return `${value.toLocaleString()} MMK`
}

export function formatMMKShort(value: number): string {
  if (value >= 100000) {
    const lakhs = value / 100000
    return `${lakhs % 1 === 0 ? lakhs.toFixed(0) : lakhs.toFixed(1)}L`
  }
  return value.toLocaleString()
}

export type UserRole = 'super_admin' | 'admin' | 'director' | 'sales_manager' | 'asst_sales_manager' | 'sales_executive' | 'chief_engineer' | 'operation_manager' | 'engineer' | 'technician' | 'finance' | 'procurement' | 'storekeeper' | 'hr';

export const ALL_ROLES: UserRole[] = ['super_admin', 'admin', 'director', 'sales_manager', 'asst_sales_manager', 'sales_executive', 'chief_engineer', 'operation_manager', 'engineer', 'technician', 'finance', 'procurement', 'storekeeper', 'hr'];
export const ADMIN_ROLES: UserRole[] = ['super_admin', 'admin'];
export const SALES_ROLES: UserRole[] = ['sales_manager', 'asst_sales_manager', 'sales_executive'];
export const ENGINEERING_ROLES: UserRole[] = ['chief_engineer', 'operation_manager', 'engineer', 'technician', 'procurement', 'storekeeper'];
export const CRM_ACCESS_ROLES: UserRole[] = [...ADMIN_ROLES, 'director', ...SALES_ROLES, 'finance'];
export const CRM_WRITE_ROLES: UserRole[] = [...ADMIN_ROLES, 'director', ...SALES_ROLES];
export const EXCEL_IMPORT_ROLES: UserRole[] = [...ADMIN_ROLES, 'director', ...SALES_ROLES];
export const USER_MGMT_ROLES: UserRole[] = [...ADMIN_ROLES];

export interface User {
  id: number;
  username: string;
  full_name: string;
  email: string;
  role: UserRole;
  is_active: boolean;
  team_id?: number | null;
  reports_to_id?: number | null;
  team?: { id: number; name: string } | null;
}

export interface Team {
  id: number;
  name: string;
  manager_id: number | null;
  manager: (User & { team_id?: number | null }) | null;
  members: (User & { team_id?: number | null })[];
  member_count: number;
  created_at: string;
  department_name?: string | null;
  division_title?: string | null;
  divisions?: Team[];
}

export const teamsApi = {
  list: (): Promise<Team[]> => api.get('/teams').then(r => r.data),
  create: (name: string): Promise<Team> => api.post('/teams', { name }).then(r => r.data),
  rename: (id: number, name: string): Promise<Team> => api.patch(`/teams/${id}`, { name }).then(r => r.data),
  delete: (id: number) => api.delete(`/teams/${id}`).then(r => r.data),
  addMember: (teamId: number, userId: number) => api.post(`/teams/${teamId}/members`, { user_id: userId }).then(r => r.data),
  removeMember: (teamId: number, userId: number) => api.delete(`/teams/${teamId}/members/${userId}`).then(r => r.data),
  setManager: (teamId: number, userId: number): Promise<Team> => api.patch(`/teams/${teamId}/manager`, { user_id: userId }).then(r => r.data),
  clearManager: (teamId: number): Promise<Team> => api.delete(`/teams/${teamId}/manager`).then(r => r.data),
  setSupervisor: (teamId: number, userId: number, supervisorId: number | null) =>
    api.patch(`/teams/${teamId}/members/${userId}/supervisor`, { supervisor_id: supervisorId }).then(r => r.data),
};

export const PERMISSION_KEYS = [
  'crm_view',
  'crm_write',
  'customer_db_write',
  'project_bank_create',
  'project_bank_edit',
  'sr_ss_create',
  'sr_ss_edit_all',
  'billing',
  'handover_write',
  'finance_view_all',
  'finance_approve',
  'analytics_finance',
  'weekly_report_view_all',
  'activity_logs_view',
] as const;

export type PermissionKey = typeof PERMISSION_KEYS[number];

export type RolePermissionsGrid = Record<string, Record<PermissionKey, boolean>>;

export interface RolePermissionsResponse {
  grid: RolePermissionsGrid;
  permission_keys: PermissionKey[];
  permission_labels: Record<PermissionKey, string>;
  roles: string[];
  role_display_names: Record<string, string>;
}

export const rolePermissionsApi = {
  get: () => api.get<RolePermissionsResponse>('/role-permissions').then(r => r.data),
  save: (grid: RolePermissionsGrid) => api.put<{ ok: boolean; grid: RolePermissionsGrid }>('/role-permissions', { grid }).then(r => r.data),
  reset: () => api.post<{ ok: boolean; grid: RolePermissionsGrid }>('/role-permissions/reset').then(r => r.data),
};

export interface Client {
  id: number;
  client_name: string;
  phone: string;
  email: string;
  company: string;
  status: string;
  source: string;
  priority: string;
  contact_type: string;
  notes: string;
  owner: string;
  created_by: number | null;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
  vip_customer?: boolean;
}

export interface DealAttachment {
  id: string;
  name: string;
  url: string;
  size: number;
  type: string;
  storage: string;
  sharepoint_path?: string;
  uploaded_at: string;
}

export interface Deal {
  id: number;
  deal_name: string;
  client_id: number | null;
  stage: string;
  value: number;
  owner: string;
  expected_close: string | null;
  notes: string;
  created_by: number | null;
  created_by_name?: string;
  created_at: string;
  updated_at: string;
  clients?: { client_name: string; company: string };
  photos?: DealAttachment[];
  documents?: DealAttachment[];
}

export interface DealActivity {
  id: number;
  deal_id: number;
  activity_type: string;
  subject: string;
  description: string;
  activity_date: string | null;
  activity_time: string;
  result: string;
  status: string;
  created_by: number | null;
  created_at: string;
}

export interface ClientActivity {
  id: number;
  client_id: number;
  activity_type: string;
  subject: string;
  description: string;
  activity_date: string | null;
  activity_time: string;
  result: string;
  status: string;
  created_by: number | null;
  created_at: string;
}

export interface ServiceRequest {
  id: number;
  sr_number: string;
  client_name: string;
  client_id: number | null;
  project_id: number | null;
  project_code: string;
  contact_number: string;
  address: string;
  service_expiry_date: string | null;
  category: string;
  priority: string;
  assigned_to: string;
  reported_date: string;
  status: string;
  description: string;
  resolution_notes: string;
  photos: any[];
  resolved_at: string | null;
  billing_type: string | null;
  billing_amount: number;
  is_billed: boolean;
  created_by: number | null;
  created_by_name: string;
  created_at: string;
  updated_at?: string;
}

export interface SiteSurvey {
  id: number;
  sr_number: string;
  client_name: string;
  client_id: number | null;
  category: string;
  priority: string;
  assigned_to: string;
  reported_date: string;
  status: string;
  description: string;
  resolution_notes: string;
  created_by: number | null;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
  contact_number?: string;
  project_type?: string;
  address?: string;
  map_link?: string;
  photos?: any[];
  documents?: any[];
}

export interface Project {
  id: number;
  project_code: string;
  project_type: string;
  client_name: string;
  client_id: number | null;
  vip_customer?: boolean;
  phone: string;
  address: string;
  date_completed: string | null;
  service_expiry_date: string | null;
  installation_start_date: string | null;
  expected_completion_date: string | null;
  assigned_to: string;
  team_members: { id: number; full_name: string; role: string }[] | null;
  description: string;
  photos: string;
  documents: string;
  created_by: number | null;
  created_by_name?: string;
  created_at: string;
  updated_at?: string;
  map_link: string;
  latitude: number | null;
  longitude: number | null;
  status: string;
  site_photos?: any[];
  pre_install_docs?: any[];
  completion_docs?: any[];
  financial_records?: InvoiceRecord[];
  _expiry_status?: 'Expired' | 'Expiring Soon';
}

export interface InvoiceFile {
  name: string;
  size: number;
  type: string;
  sharepoint_path: string;
  uploaded_at: string;
  uploaded_by: string;
}

export interface InvoiceRecord {
  id: string;
  invoice_number: string;
  amount: number;
  date: string;
  notes: string;
  files: InvoiceFile[];
}

export interface RecentSurvey {
  id: number;
  sr_number: string;
  client_name: string;
  status: string;
  reported_date: string | null;
  created_at: string;
}

export interface PaginatedResponse<T> {
  data: T[];
  total: number;
  page: number;
  per_page: number;
  total_pages: number;
}

export interface PipelineSummary {
  [stage: string]: { count: number; value: number };
}

export interface DashboardData {
  leads: number;
  contacts: number;
  pipeline_value: number;
  won_deals: number;
  won_value: number;
  open_srs: number;
  pipeline_summary: PipelineSummary;
  expiry_alerts: Project[];
  recent_srs: ServiceRequest[];
  recent_surveys: RecentSurvey[];
}

export interface SearchResults {
  clients: Client[];
  deals: Deal[];
  projects: Project[];
  service_requests: ServiceRequest[];
}

export interface ApprovalEntry {
  approver_username: string;
  approver_name: string;
  approver_role: string;
  action: 'approved' | 'rejected';
  at: string;
}

export interface LinkedProjectInfo {
  id: number;
  project_code: string;
  project_type: string;
  client_name: string;
}

export interface BudgetRequest {
  id: number;
  br_number: string;
  budget_type: string;
  title: string;
  amount: number;
  project_id: number | null;
  client_id: number | null;
  project_ids: LinkedProjectInfo[];
  additional_approvers: string[];
  approvals: ApprovalEntry[];
  status: string;
  submitted_by: number | null;
  submitted_by_name?: string;
  requested_date: string | null;
  notes: string | null;
  photos: DealAttachment[];
  documents: DealAttachment[];
  disbursed_at?: string | null;
  disbursement_reference?: string | null;
  created_at: string;
  updated_at: string;
  projects?: { project_code: string; project_type: string; client_name: string } | null;
  clients?: { client_name: string; company: string } | null;
}

export interface ACSettlement {
  id: number;
  ec_number: string;
  title: string;
  total_amount: number;
  status: string;
  submitted_by_name?: string;
  created_at: string;
}

export interface AdvanceCashRequest {
  id: number;
  ac_number: string;
  title: string;
  amount: number;
  additional_approvers: string[];
  approvals: ApprovalEntry[];
  status: string;
  submitted_by: number | null;
  submitted_by_name?: string;
  requested_date: string | null;
  notes: string | null;
  photos: DealAttachment[];
  documents: DealAttachment[];
  disbursed_at?: string | null;
  disbursement_reference?: string | null;
  settled_amount?: number;
  settlement_status?: string;
  created_at: string;
  updated_at: string;
}

export const budgetRequestApi = {
  list: (params?: { status?: string }) =>
    api.get<BudgetRequest[]>('/budget-requests', { params }),
  get: (id: number) =>
    api.get<BudgetRequest>(`/budget-requests/${id}`),
  create: (data: {
    budget_type: string;
    title: string;
    amount: number;
    project_id?: number | null;
    client_id?: number | null;
    project_ids?: number[];
    additional_approvers?: string[];
    requested_date?: string | null;
    notes?: string | null;
    photos?: File[];
    documents?: File[];
  }) => {
    const fd = new FormData();
    fd.append('budget_type', data.budget_type);
    fd.append('title', data.title);
    fd.append('amount', String(data.amount));
    if (data.project_id != null) fd.append('project_id', String(data.project_id));
    if (data.client_id != null) fd.append('client_id', String(data.client_id));
    fd.append('project_ids', JSON.stringify(data.project_ids ?? []));
    fd.append('additional_approvers', JSON.stringify(data.additional_approvers ?? []));
    if (data.requested_date) fd.append('requested_date', data.requested_date);
    if (data.notes) fd.append('notes', data.notes);
    for (const f of data.photos ?? []) fd.append('photos', f);
    for (const f of data.documents ?? []) fd.append('documents', f);
    return api.post<BudgetRequest>('/budget-requests', fd, {
      headers: { 'Content-Type': undefined },
    });
  },
  approve: (id: number) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/budget-requests/${id}/approve`),
  reject: (id: number, reason?: string) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/budget-requests/${id}/reject`, { reason }),
  revokeApproval: (id: number) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/budget-requests/${id}/revoke-approval`),
  uploadPhoto: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/budget-requests/${id}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (id: number, photoId: string) =>
    api.delete<{ ok: boolean }>(`/budget-requests/${id}/photos/${photoId}`),
  uploadDocument: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/budget-requests/${id}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (id: number, docId: string) =>
    api.delete<{ ok: boolean }>(`/budget-requests/${id}/documents/${docId}`),
  disburse: (id: number, reference?: string) =>
    api.patch<{ ok: boolean; status: string; disbursed_at: string }>(`/budget-requests/${id}/disburse`, { reference }),
  summary: () =>
    api.get<{ count_pending: number; count_approved: number; count_disbursed: number; count_rejected: number; total_pending: number; total_approved: number; total_disbursed: number; total_rejected: number }>('/budget-requests/summary'),
  deleteBR: (id: number) =>
    api.delete<{ ok: boolean }>(`/budget-requests/${id}`),
  getFileSrc: (file: DealAttachment, brId: number): string => {
    if (file.storage === 'sharepoint' && file.sharepoint_path) {
      const token = localStorage.getItem('token');
      const base = `/api/budget-requests/${brId}/download?path=${encodeURIComponent(file.sharepoint_path)}`;
      return token ? `${base}&token=${encodeURIComponent(token)}` : base;
    }
    return file.url || '';
  },
};

export interface ExpenseClaimItem {
  id?: number;
  expense_claim_id?: number;
  expense_type: string;
  description: string;
  amount: number;
  project_id?: number | null;
  client_id?: number | null;
  project_ids?: LinkedProjectInfo[];
  sort_order?: number;
  projects?: { project_code: string; project_type: string; client_name: string } | null;
  clients?: { client_name: string; company: string } | null;
}

export interface ExpenseClaim {
  id: number;
  ec_number: string;
  title: string;
  total_amount: number;
  additional_approvers: string[];
  approvals: ApprovalEntry[];
  status: string;
  submitted_by: number | null;
  submitted_by_name?: string;
  requested_date: string | null;
  notes: string | null;
  photos: DealAttachment[];
  documents: DealAttachment[];
  items?: ExpenseClaimItem[];
  project_ids?: LinkedProjectInfo[];
  contact_id?: number | null;
  contact_label?: string;
  disbursed_at?: string | null;
  disbursement_reference?: string | null;
  claim_type?: string;
  linked_ac_id?: number | null;
  linked_ac_number?: string | null;
  created_at: string;
  updated_at: string;
}

export const expenseClaimApi = {
  list: (params?: { status?: string }) =>
    api.get<ExpenseClaim[]>('/expense-claims', { params }),
  get: (id: number) =>
    api.get<ExpenseClaim>(`/expense-claims/${id}`),
  create: (data: {
    title: string;
    additional_approvers?: string[];
    requested_date?: string | null;
    notes?: string | null;
    items: Array<{
      expense_type: string;
      description: string;
      amount: number;
      sort_order?: number;
    }>;
    project_ids?: number[];
    contact_id?: number | null;
    draft?: boolean;
    photos?: File[];
    documents?: File[];
    claim_type?: string;
    linked_ac_id?: number | null;
  }) => {
    const fd = new FormData();
    fd.append('title', data.title);
    fd.append('additional_approvers', JSON.stringify(data.additional_approvers ?? []));
    if (data.requested_date) fd.append('requested_date', data.requested_date);
    if (data.notes) fd.append('notes', data.notes);
    fd.append('items', JSON.stringify(data.items));
    fd.append('claim_project_ids', JSON.stringify(data.project_ids ?? []));
    if (data.contact_id != null) fd.append('claim_contact_id', String(data.contact_id));
    if (data.draft) fd.append('draft', 'true');
    if (data.claim_type) fd.append('claim_type', data.claim_type);
    if (data.linked_ac_id != null) fd.append('linked_ac_id', String(data.linked_ac_id));
    for (const f of data.photos ?? []) fd.append('photos', f);
    for (const f of data.documents ?? []) fd.append('documents', f);
    return api.post<ExpenseClaim>('/expense-claims', fd, {
      headers: { 'Content-Type': undefined },
    });
  },
  submit: (id: number) =>
    api.post<ExpenseClaim>(`/expense-claims/${id}/submit`),
  approve: (id: number) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/expense-claims/${id}/approve`),
  reject: (id: number, reason?: string) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/expense-claims/${id}/reject`, { reason }),
  revokeApproval: (id: number) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/expense-claims/${id}/revoke-approval`),
  uploadPhoto: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/expense-claims/${id}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (id: number, photoId: string) =>
    api.delete<{ ok: boolean }>(`/expense-claims/${id}/photos/${photoId}`),
  uploadDocument: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/expense-claims/${id}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (id: number, docId: string) =>
    api.delete<{ ok: boolean }>(`/expense-claims/${id}/documents/${docId}`),
  disburse: (id: number, reference?: string) =>
    api.patch<{ ok: boolean; status: string; disbursed_at: string }>(`/expense-claims/${id}/disburse`, { reference }),
  summary: () =>
    api.get<{ count_pending: number; count_approved: number; count_disbursed: number; count_rejected: number; total_pending: number; total_approved: number; total_disbursed: number; total_rejected: number }>('/expense-claims/summary'),
  deleteEC: (id: number) =>
    api.delete<{ ok: boolean }>(`/expense-claims/${id}`),
  getFileSrc: (file: DealAttachment, ecId: number): string => {
    if (file.storage === 'sharepoint' && file.sharepoint_path) {
      const token = localStorage.getItem('token');
      const base = `/api/expense-claims/${ecId}/download?path=${encodeURIComponent(file.sharepoint_path)}`;
      return token ? `${base}&token=${encodeURIComponent(token)}` : base;
    }
    return file.url || '';
  },
};

export const advanceCashApi = {
  list: (params?: { status?: string }) =>
    api.get<AdvanceCashRequest[]>('/advance-cash', { params }),
  get: (id: number) =>
    api.get<AdvanceCashRequest>(`/advance-cash/${id}`),
  create: (data: {
    title: string;
    amount: number;
    additional_approvers?: string[];
    requested_date?: string | null;
    notes?: string | null;
    photos?: File[];
    documents?: File[];
  }) => {
    const fd = new FormData();
    fd.append('title', data.title);
    fd.append('amount', String(data.amount));
    fd.append('additional_approvers', JSON.stringify(data.additional_approvers ?? []));
    if (data.requested_date) fd.append('requested_date', data.requested_date);
    if (data.notes) fd.append('notes', data.notes);
    for (const f of data.photos ?? []) fd.append('photos', f);
    for (const f of data.documents ?? []) fd.append('documents', f);
    return api.post<AdvanceCashRequest>('/advance-cash', fd, {
      headers: { 'Content-Type': undefined },
    });
  },
  approve: (id: number) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/advance-cash/${id}/approve`),
  reject: (id: number, reason?: string) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/advance-cash/${id}/reject`, { reason }),
  revokeApproval: (id: number) =>
    api.patch<{ ok: boolean; status: string; approvals: ApprovalEntry[] }>(`/advance-cash/${id}/revoke-approval`),
  disburse: (id: number, reference?: string) =>
    api.patch<{ ok: boolean; status: string; disbursed_at: string }>(`/advance-cash/${id}/disburse`, { reference }),
  summary: () =>
    api.get<{ count_pending: number; count_approved: number; count_disbursed: number; count_rejected: number; total_pending: number; total_approved: number; total_disbursed: number; total_rejected: number }>('/advance-cash/summary'),
  uploadPhoto: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/advance-cash/${id}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (id: number, photoId: string) =>
    api.delete<{ ok: boolean }>(`/advance-cash/${id}/photos/${photoId}`),
  uploadDocument: (id: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/advance-cash/${id}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (id: number, docId: string) =>
    api.delete<{ ok: boolean }>(`/advance-cash/${id}/documents/${docId}`),
  deleteAC: (id: number) =>
    api.delete<{ ok: boolean }>(`/advance-cash/${id}`),
  settlements: (id: number) =>
    api.get<ACSettlement[]>(`/advance-cash/${id}/settlements`),
  getFileSrc: (file: DealAttachment, acId: number): string => {
    if (file.storage === 'sharepoint' && file.sharepoint_path) {
      const token = localStorage.getItem('token');
      const base = `/api/advance-cash/${acId}/download?path=${encodeURIComponent(file.sharepoint_path)}`;
      return token ? `${base}&token=${encodeURIComponent(token)}` : base;
    }
    return file.url || '';
  },
};

export const dealAttachmentApi = {
  uploadPhoto: (dealId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/deals/${dealId}/photos`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deletePhoto: (dealId: number, photoId: string) =>
    api.delete<{ ok: boolean }>(`/deals/${dealId}/photos/${photoId}`),
  uploadDocument: (dealId: number, file: File) => {
    const fd = new FormData();
    fd.append('file', file);
    return api.post<{ ok: boolean; file: DealAttachment }>(`/deals/${dealId}/documents`, fd, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
  },
  deleteDocument: (dealId: number, docId: string) =>
    api.delete<{ ok: boolean }>(`/deals/${dealId}/documents/${docId}`),
  getFileSrc: (file: DealAttachment, dealId: number): string => {
    if (file.storage === 'sharepoint' && file.sharepoint_path) {
      const token = localStorage.getItem('token');
      const base = `/api/deals/${dealId}/download?path=${encodeURIComponent(file.sharepoint_path)}`;
      return token ? `${base}&token=${encodeURIComponent(token)}` : base;
    }
    return file.url || '';
  },
};
